/**
 * ProjectTeamHomepage — DB-4
 *
 * Homepage for users with the 'Project Team' platform role.
 *
 * Per PRD §7.3:
 *   - Shows the project list pre-filtered to their allocated projects
 *   - Filter is locked (cannot be removed)
 *   - Financial columns are hidden (enforced by their Project Tabs permissions)
 *   - Links to individual project detail pages (with tabs hidden per role)
 *
 * Renders the full ProjectsPage (with header hidden) so the user sees their
 * allocated projects inline on the homepage. The scope filtering is driven by
 * the user's `projectVisibility` permission ('managed-allocated') which
 * ProjectsPage already respects via PermissionsContext.
 */

import { Lock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ProjectsPage } from '@/pages/projects';
import { usePermissions } from '@/contexts/PermissionsContext';

// ── Props ─────────────────────────────────────────────────────────────────────

interface ProjectTeamHomepageProps {
  onNavigate?: (page: string) => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function ProjectTeamHomepage({ onNavigate }: ProjectTeamHomepageProps) {
  const { projectVisibility } = usePermissions();

  // The locked-filter banner only makes sense when the user's role actually
  // restricts visibility to their managed/allocated projects. When the role's
  // Project Visibility is 'all', the list shows every project so the "allocated
  // projects only" message would be misleading and must not be shown.
  const showLockedFilterBanner = projectVisibility === 'managed-allocated';

  return (
    <div className="space-y-4">
      {/* Locked filter banner — only when visibility is restricted */}
      {showLockedFilterBanner && (
        <Alert className="border-warning-200 bg-warning-50 dark:border-warning-800/40 dark:bg-warning-900/10">
          <Lock className="h-4 w-4 text-warning-600 dark:text-warning-400" />
          <AlertDescription className="text-warning-800 dark:text-warning-300 text-sm flex items-center gap-2">
            <span>
              Showing <strong>your allocated projects only.</strong> Contact your Project Manager to
              be added to additional projects.
            </span>
            <Badge
              variant="outline"
              className="text-warning-700 border-warning-300 bg-warning-100 dark:text-warning-300 dark:border-warning-700 dark:bg-warning-900/20 text-xs font-normal gap-1 flex-shrink-0"
            >
              <Lock className="h-2.5 w-2.5" />
              Filter locked
            </Badge>
          </AlertDescription>
        </Alert>
      )}

      {/* Render the full project list with its header hidden —
          ProjectsPage respects projectVisibility from PermissionsContext
          so only allocated projects are shown for this role. */}
      <ProjectsPage hideHeader />
    </div>
  );
}
